import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Switch,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import StorageIcon from '@mui/icons-material/Storage';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToAllToolAccess,
  grantToolAccess,
  revokeToolAccess,
  subscribeToRecentActivity,
  AVAILABLE_TOOLS,
} from '../services/adminService';
import { getAllOrganizations } from '../services/organizationService';
import { getAllSurveys } from '../services/surveyService';
import { seedChevronCaseStudy, getCaseStudyById } from '../services/caseStudyService';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TablePagination from '@mui/material/TablePagination';

function Admin() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Admin Panel
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage users, tool access, organizations, and view activity logs.
      </Typography>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Users & Tool Access" />
        <Tab icon={<BusinessIcon />} iconPosition="start" label="Organizations" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Activity Log" />
        <Tab icon={<StorageIcon />} iconPosition="start" label="Data Management" />
      </Tabs>

      {tab === 0 && <UsersTab />}
      {tab === 1 && <OrganizationsTab />}
      {tab === 2 && <ActivityLogTab />}
      {tab === 3 && <DataManagementTab />}
    </Box>
  );
}

// ============================================================
// Users & Tool Access Tab (Features #34 & #35)
// ============================================================
function UsersTab() {
  const { user: currentUser } = useAuth();
  const [toolAccessRecords, setToolAccessRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToAllToolAccess((records) => {
      setToolAccessRecords(records);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Filter users by search query
  const filteredRecords = useMemo(() => {
    if (!search.trim()) return toolAccessRecords;
    const q = search.toLowerCase();
    return toolAccessRecords.filter((r) => {
      const uid = r.uid?.toLowerCase() || '';
      const email = r.email?.toLowerCase() || '';
      return uid.includes(q) || email.includes(q);
    });
  }, [toolAccessRecords, search]);

  const handleToggleAccess = (record, toolSlug, currentlyHasAccess) => {
    const action = currentlyHasAccess ? 'revoke' : 'grant';
    const toolName = AVAILABLE_TOOLS.find((t) => t.slug === toolSlug)?.name || toolSlug;
    setConfirmDialog({
      record,
      toolSlug,
      action,
      toolName,
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog) return;
    const { record, toolSlug, action } = confirmDialog;
    setProcessing(`${record.uid}-${toolSlug}`);
    setConfirmDialog(null);

    try {
      if (action === 'grant') {
        await grantToolAccess(record.uid, toolSlug, currentUser.uid);
      } else {
        await revokeToolAccess(record.uid, toolSlug, currentUser.uid);
      }
      setSnackbar({
        open: true,
        message: `Tool access ${action === 'grant' ? 'granted' : 'revoked'} successfully.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to update tool access:', err);
      setSnackbar({
        open: true,
        message: `Failed to ${action} tool access: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by user ID or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {filteredRecords.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {search ? 'No users match your search.' : 'No users with tool access records found.'}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>User ID</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    {AVAILABLE_TOOLS.map((tool) => (
                      <TableCell key={tool.slug} align="center">
                        <strong>{tool.name}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.map((record) => {
                    return (
                      <TableRow key={record.uid} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {record.uid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {record.email || '—'}
                          </Typography>
                        </TableCell>
                        {AVAILABLE_TOOLS.map((tool) => {
                          const toolData = record.tools?.[tool.slug];
                          const hasAccess = toolData?.hasAccess === true;
                          const isProcessing = processing === `${record.uid}-${tool.slug}`;

                          return (
                            <TableCell key={tool.slug} align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                {isProcessing ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <>
                                    <Switch
                                      checked={hasAccess}
                                      onChange={() => handleToggleAccess(record, tool.slug, hasAccess)}
                                      size="small"
                                      color="success"
                                    />
                                    <Chip
                                      icon={hasAccess ? <CheckCircleIcon /> : <BlockIcon />}
                                      label={hasAccess ? 'Granted' : 'Revoked'}
                                      color={hasAccess ? 'success' : 'default'}
                                      size="small"
                                      variant={hasAccess ? 'filled' : 'outlined'}
                                    />
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {filteredRecords.length} user{filteredRecords.length !== 1 ? 's' : ''} found
          </Typography>
        </CardContent>
      </Card>

      {/* Confirmation Dialog (#35) */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>
          {confirmDialog?.action === 'grant' ? 'Grant' : 'Revoke'} Tool Access
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{' '}
            <strong>{confirmDialog?.action}</strong> access to{' '}
            <strong>{confirmDialog?.toolName}</strong> for user{' '}
            <strong>{confirmDialog?.record?.uid}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmToggle}
            variant="contained"
            color={confirmDialog?.action === 'grant' ? 'success' : 'error'}
          >
            {confirmDialog?.action === 'grant' ? 'Grant Access' : 'Revoke Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error feedback (#35) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// ============================================================
// Organizations Tab (Feature #36 - View All Organizations)
// ============================================================

/** Expandable row for a single organization */
function OrgRow({ org, surveys }) {
  const [open, setOpen] = useState(false);

  // Count members: admins array length represents org members
  const memberCount = org.admins?.length || 0;

  // Filter surveys for this org
  const orgSurveys = useMemo(() => {
    return surveys.filter((s) => s.organizationId === org.id);
  }, [surveys, org.id]);

  const activeSurveys = orgSurveys.filter((s) => s.status === 'active').length;
  const closedSurveys = orgSurveys.filter((s) => s.status === 'closed').length;
  const draftSurveys = orgSurveys.filter((s) => s.status === 'draft').length;

  // Recent surveys (last 5)
  const recentSurveys = orgSurveys.slice(0, 5);

  return (
    <React.Fragment>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'unset' : undefined } }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell><strong>{org.name}</strong></TableCell>
        <TableCell>{org.industry || '—'}</TableCell>
        <TableCell align="center">
          <Chip label={memberCount} size="small" variant="outlined" />
        </TableCell>
        <TableCell align="center">{org.sites?.length || 0}</TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {activeSurveys > 0 && <Chip label={`${activeSurveys} active`} size="small" color="success" variant="outlined" />}
            {draftSurveys > 0 && <Chip label={`${draftSurveys} draft`} size="small" color="default" variant="outlined" />}
            {closedSurveys > 0 && <Chip label={`${closedSurveys} closed`} size="small" color="warning" variant="outlined" />}
            {orgSurveys.length === 0 && <Typography variant="caption" color="text.secondary">None</Typography>}
          </Box>
        </TableCell>
        <TableCell>
          {org.createdAt?.toDate
            ? org.createdAt.toDate().toLocaleDateString()
            : '—'}
        </TableCell>
      </TableRow>

      {/* Expandable detail row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              {/* Org details grid */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                {/* Hierarchy details */}
                <Card variant="outlined" sx={{ flex: '1 1 280px', minWidth: 280 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Organization Structure
                    </Typography>
                    <Typography variant="body2"><strong>Sites:</strong> {org.sites?.length || 0}</Typography>
                    {org.sites?.length > 0 && (
                      <Box sx={{ pl: 2, mt: 0.5 }}>
                        {org.sites.map((site) => (
                          <Typography key={site.id} variant="body2" color="text.secondary">
                            • {site.name}{site.location ? ` (${site.location})` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Departments:</strong> {org.departments?.length || 0}</Typography>
                    {org.departments?.length > 0 && (
                      <Box sx={{ pl: 2, mt: 0.5 }}>
                        {org.departments.map((dept) => (
                          <Typography key={dept.id} variant="body2" color="text.secondary">
                            • {dept.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Teams:</strong> {org.teams?.length || 0}</Typography>
                    {org.teams?.length > 0 && (
                      <Box sx={{ pl: 2, mt: 0.5 }}>
                        {org.teams.map((team) => (
                          <Typography key={team.id} variant="body2" color="text.secondary">
                            • {team.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Admins:</strong> {memberCount}</Typography>
                    {org.admins?.length > 0 && (
                      <Box sx={{ pl: 2, mt: 0.5 }}>
                        {org.admins.map((uid, idx) => (
                          <Typography key={idx} variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            • {uid}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {org.thresholds && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}><strong>OE Thresholds:</strong></Typography>
                        <Box sx={{ pl: 2, mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`R: ${org.thresholds.reliability ?? 6}`} size="small" color="error" variant="outlined" />
                          <Chip label={`E: ${org.thresholds.efficiency ?? 6}`} size="small" color="warning" variant="outlined" />
                          <Chip label={`P: ${org.thresholds.productivity ?? 6}`} size="small" color="info" variant="outlined" />
                          <Chip label={`S: ${org.thresholds.safety ?? 6}`} size="small" color="success" variant="outlined" />
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Recent survey activity */}
                <Card variant="outlined" sx={{ flex: '1 1 320px', minWidth: 320 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      <AssignmentIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Recent Survey Activity ({orgSurveys.length} total)
                    </Typography>
                    {recentSurveys.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No surveys created yet.
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {recentSurveys.map((survey, idx) => (
                          <React.Fragment key={survey.id}>
                            <ListItem disableGutters sx={{ px: 0 }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                      {survey.title}
                                    </Typography>
                                    <Chip
                                      label={survey.status}
                                      size="small"
                                      color={
                                        survey.status === 'active' ? 'success' :
                                        survey.status === 'closed' ? 'warning' :
                                        'default'
                                      }
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={
                                  survey.createdAt?.toDate
                                    ? `Created ${survey.createdAt.toDate().toLocaleDateString()}`
                                    : ''
                                }
                              />
                            </ListItem>
                            {idx < recentSurveys.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function OrganizationsTab() {
  const [orgs, setOrgs] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgData, surveyData] = await Promise.all([
          getAllOrganizations(),
          getAllSurveys(),
        ]);
        setOrgs(orgData);
        setSurveys(surveyData);
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {orgs.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No organizations found.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Industry</strong></TableCell>
                  <TableCell align="center"><strong>Members</strong></TableCell>
                  <TableCell align="center"><strong>Sites</strong></TableCell>
                  <TableCell align="center"><strong>Surveys</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgs.map((org) => (
                  <OrgRow key={org.id} org={org} surveys={surveys} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {orgs.length} organization{orgs.length !== 1 ? 's' : ''} total
        </Typography>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Activity Log Tab (Feature #37 - Global Activity Log)
// ============================================================
function ActivityLogTab() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    // Fetch a larger set for client-side filtering and pagination
    const unsubscribe = subscribeToRecentActivity((entries) => {
      setActivities(entries);
      setLoading(false);
    }, 500);
    return unsubscribe;
  }, []);

  // Derive unique users and action types for filter dropdowns
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    activities.forEach((a) => { if (a.userId) users.add(a.userId); });
    return Array.from(users).sort();
  }, [activities]);

  const uniqueActions = useMemo(() => {
    const actions = new Set();
    activities.forEach((a) => { if (a.action) actions.add(a.action); });
    return Array.from(actions).sort();
  }, [activities]);

  // Apply filters
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    if (userFilter) {
      filtered = filtered.filter((a) => a.userId === userFilter);
    }

    if (actionFilter) {
      filtered = filtered.filter((a) => a.action === actionFilter);
    }

    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((a) => {
        if (!a.createdAt?.toDate) return false;
        return a.createdAt.toDate() >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((a) => {
        if (!a.createdAt?.toDate) return false;
        return a.createdAt.toDate() <= toDate;
      });
    }

    return filtered;
  }, [activities, userFilter, actionFilter, dateFrom, dateTo]);

  // Paginate
  const paginatedActivities = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredActivities.slice(start, start + rowsPerPage);
  }, [filteredActivities, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [userFilter, actionFilter, dateFrom, dateTo]);

  const hasFilters = userFilter || actionFilter || dateFrom || dateTo;

  const clearFilters = () => {
    setUserFilter('');
    setActionFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const getActionColor = (action) => {
    if (action?.includes('grant')) return 'success';
    if (action?.includes('revoke')) return 'error';
    if (action?.includes('created')) return 'info';
    if (action?.includes('completed')) return 'primary';
    if (action?.includes('status')) return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Filter controls */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2">Filters</Typography>
            {hasFilters && (
              <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* User filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                label="User"
              >
                <MenuItem value="">
                  <em>All Users</em>
                </MenuItem>
                {uniqueUsers.map((uid) => (
                  <MenuItem key={uid} value={uid}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {uid}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Action type filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                label="Action Type"
              >
                <MenuItem value="">
                  <em>All Actions</em>
                </MenuItem>
                {uniqueActions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date range filters */}
            <TextField
              size="small"
              type="date"
              label="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              type="date"
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Activity entries */}
        {filteredActivities.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {hasFilters ? 'No activities match the current filters.' : 'No activity log entries found.'}
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Action</strong></TableCell>
                    <TableCell><strong>User</strong></TableCell>
                    <TableCell><strong>Tool</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                    <TableCell><strong>Date/Time</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedActivities.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Chip
                          label={entry.action}
                          size="small"
                          color={getActionColor(entry.action)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {entry.userId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {entry.toolSlug ? (
                          <Chip label={entry.toolSlug} size="small" variant="outlined" />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.details?.targetUser && `Target: ${entry.details.targetUser}`}
                          {entry.details?.title && `${entry.details.title}`}
                          {entry.details?.name && `${entry.details.name}`}
                          {entry.details?.surveyId && !entry.details?.title && `Survey: ${entry.details.surveyId}`}
                          {!entry.details?.targetUser && !entry.details?.title && !entry.details?.name && !entry.details?.surveyId && '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.createdAt?.toDate
                            ? entry.createdAt.toDate().toLocaleString()
                            : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredActivities.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {hasFilters
            ? `Showing ${filteredActivities.length} of ${activities.length} total activities (filtered)`
            : `${activities.length} total activities`}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Data Management Tab - Seed Data & Case Studies
// ============================================================
function DataManagementTab() {
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [chevronExists, setChevronExists] = useState(null);
  const [checkingExists, setCheckingExists] = useState(true);

  useEffect(() => {
    // Check if Chevron case study already exists
    getCaseStudyById('chevron-richmond-refinery-2012')
      .then((doc) => {
        setChevronExists(!!doc);
        setCheckingExists(false);
      })
      .catch(() => {
        setChevronExists(false);
        setCheckingExists(false);
      });
  }, [seedResult]);

  const handleSeedChevron = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const docId = await seedChevronCaseStudy();
      setSeedResult({ success: true, message: `Chevron case study seeded successfully (ID: ${docId})` });
    } catch (error) {
      setSeedResult({ success: false, message: `Failed to seed: ${error.message}` });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Seed Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Pre-populate reference data for development and testing. These operations are idempotent and can be run multiple times safely.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Chevron Richmond Refinery Case Study
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            CSB Final Investigation Report 2012-03-I-CA (January 2015). Includes full REPS mapping,
            OE element analysis, key findings, lessons learned, and precursor patterns.
          </Typography>

          {checkingExists ? (
            <CircularProgress size={20} sx={{ mt: 1 }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSeedChevron}
                disabled={seeding}
                startIcon={seeding ? <CircularProgress size={16} /> : <StorageIcon />}
              >
                {chevronExists ? 'Re-Seed Chevron Case Study' : 'Seed Chevron Case Study'}
              </Button>
              {chevronExists && (
                <Chip label="Already exists" color="success" size="small" icon={<CheckCircleIcon />} />
              )}
            </Box>
          )}

          {seedResult && (
            <Alert severity={seedResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {seedResult.message}
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default Admin;
